import React, { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { File, Trash2, Users } from "lucide-react";
import { cn } from "lib/utils";
import { useTargeting, type TargetingArea } from "./TargetingContext";
import { useAreaCardFocus } from "./useAreaCardFocus";
import { useMinLeadTimeBusinessDays } from "campaign-builder/hooks/useMinLeadTimeBusinessDays";
import { getEarliestLaunchDate } from "campaign-builder/state/launchDate";
import {
  addScheduleDates,
  computeAreaTotals,
  formatScheduleDate,
  removeScheduleDate,
  removeScheduleDates,
  toggleScheduleTime,
} from "campaign-builder/state/h2hSchedule";
import { DistributionDatePicker } from "./DistributionDatePicker";
import { DistributionTimesSelect } from "./DistributionTimesSelect";

// One H2H distribution-area card (AC27-AC30): location header with consolidated
// distributor/flyer totals, an inline-calendar date selector, and a per-date
// table of multi-select time chips. Row trash deletes a date silently; the card
// trash asks for confirmation before removing the whole area.
export const H2hAreaCard = ({ area }: { area: TargetingArea }) => {
  const { setAreaSchedule, removeArea, focusOnMap, baPerLocation, flyerPack, hoveredAreaId, setHoveredArea } =
    useTargeting();
  const { ref, focused } = useAreaCardFocus(area.id);
  const hovered = hoveredAreaId === area.id;
  const minDate = getEarliestLaunchDate(useMinLeadTimeBusinessDays());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const schedule = area.schedule ?? [];
  const { distributors, flyers } = computeAreaTotals(schedule, baPerLocation, flyerPack);

  const setSchedule = (next: typeof schedule) => setAreaSchedule(area.id, next);

  return (
    <div
      ref={ref}
      data-cypress-id={`targeting-area-card-${area.id}`}
      // Hovering the card highlights its pin on the map and darkens this outline (AC24).
      onMouseEnter={() => setHoveredArea(area.id)}
      onMouseLeave={() => setHoveredArea(null)}
      className={cn(
        "flex scroll-my-6 flex-col gap-3 rounded-md border border-solid bg-background p-4 transition-all duration-700",
        focused
          ? "border-primary ring-2 ring-primary/40"
          : hovered
          ? "border-slate-400 dark:border-slate-500"
          : "border-border"
      )}
    >
      {/* Header: clicking the location name/area pans the map to its pin (AC23). */}
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => focusOnMap(area.id)}
          className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-foreground hover:text-primary"
        >
          {area.value}
        </button>
        <div className="flex shrink-0 items-center gap-4">
          {schedule.length > 0 && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <File className="size-4" />
                {flyers.toLocaleString()} flyers
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="size-4" />
                {distributors.toLocaleString()} distributors
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            aria-label="Remove area"
            data-cypress-id={`targeting-area-remove-${area.id}`}
            className="text-foreground transition-colors hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>

      <DistributionDatePicker
        schedule={schedule}
        minDate={minDate}
        onAddDates={(dates) => setSchedule(addScheduleDates(schedule, dates))}
        onRemoveDates={(dates) => setSchedule(removeScheduleDates(schedule, dates))}
        cypressId={`targeting-area-date-picker-${area.id}`}
      />

      {schedule.length > 0 && (
        <div className="overflow-hidden rounded-md border border-solid border-border">
          <div className="flex items-center gap-3 bg-muted/60 px-3 py-2 text-xs font-medium text-muted-foreground">
            <span className="w-28 shrink-0">Date</span>
            <span className="flex-1">Distribution times</span>
            <span className="w-8 shrink-0" />
          </div>
          {schedule.map((entry, i) => (
            <div
              key={entry.date}
              className="flex items-center gap-3 border-0 border-t border-solid border-border px-3 py-2"
            >
              <span className="w-28 shrink-0 text-sm text-foreground">{formatScheduleDate(entry.date)}</span>
              <div className="flex-1">
                <DistributionTimesSelect
                  value={entry.times}
                  onToggle={(slot) => setSchedule(toggleScheduleTime(schedule, entry.date, slot))}
                  // Row-indexed (not date-keyed) so the selector stays stable for
                  // automation regardless of which dates are picked (T10).
                  cypressId={`targeting-area-times-${area.id}-row-${i}`}
                />
              </div>
              <button
                type="button"
                onClick={() => setSchedule(removeScheduleDate(schedule, entry.date))}
                aria-label={`Remove ${formatScheduleDate(entry.date)}`}
                data-cypress-id={`targeting-area-date-remove-${area.id}-row-${i}`}
                className="flex w-8 shrink-0 justify-center text-foreground transition-colors hover:text-destructive"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* AC30: whole-card removal is confirmed; row removal is silent. Built on
          radix primitives directly so the light scrim and the subtle centred
          fade/zoom can be controlled per-instance (the shared Dialog bakes a dark
          overlay + corner-flying animation). */}
      <DialogPrimitive.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="cb-alert-overlay fixed inset-0 z-[1350] bg-[rgba(0,0,0,0.25)]" />
          <DialogPrimitive.Content className="cb-alert fixed left-1/2 top-1/2 z-[1350] w-full max-w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-solid border-border bg-background p-6 shadow-xl focus:outline-none">
            <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
              Remove distribution area?
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-1.5 text-sm text-muted-foreground">
              {area.value} will be removed from your distribution list. You can add it back any time.
            </DialogPrimitive.Description>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                data-cypress-id={`targeting-area-remove-cancel-${area.id}`}
                className="inline-flex h-9 items-center justify-center rounded-md border border-solid border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-slate-50 dark:hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmOpen(false);
                  removeArea(area.id);
                }}
                data-cypress-id={`targeting-area-remove-confirm-${area.id}`}
                className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Remove area
              </button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
};
